/*
  PCG cert lookup (2026-10 header redesign).

  The Population Report search modal, moved out of sections/Count-timer-section.liquid so it
  works on every page. Markup: snippets/pcg-cert-search-modal.liquid (rendered by the header).
  It is opened by:
  - any [data-pcg-cert-open] control (the header's cert pill and phone icon, the drawer field);
  - Ctrl+K / Cmd+K, and "/" outside a text field;
  - typing in the home page's search panel ([data-pcg-cert-input] in Count-timer-section),
    which keeps its old behaviour: after a pause, the modal opens on what was typed.
  window.PCGCertSearch.open(term) / .close() are there for anything else.

  Search, paging and results are the original code: public /v1/gradings, 10 per page.
*/
(function () {
  if (window.PCGCertSearch) return;

  const SEARCH_API = 'https://app.premiercardgrading.com/public/api/v1/gradings';

  let searchState = {
    currentPage: 0,
    totalPages: 0,
    totalElements: 0,
    currentSearchTerm: '',
    isLoading: false,
    itemsPerPage: 10,
    searchTimeout: null
  };

  let modal = null;
  let modalSearchInput = null;
  let lastOpener = null;

  const $ = (selector) => (modal ? modal.querySelector(selector) : null);

  function esc(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  function updateModalTitle(searchTerm) {
    const modalTitle = $('.modal-title');
    if (modalTitle) {
      const titlePrefix = modal.dataset.modalTitle || 'Search Results for:';
      modalTitle.textContent = `${titlePrefix} ${searchTerm}`;
    }
  }

  const SearchStateManager = {
    listeners: new Set(),

    getState() {
      return { ...searchState };
    },

    setState(newState) {
      searchState = { ...searchState, ...newState };
      this.notifyListeners();
    },

    subscribe(listener) {
      this.listeners.add(listener);
      return () => this.listeners.delete(listener);
    },

    notifyListeners() {
      this.listeners.forEach(listener => listener(searchState));
    }
  };

  // Helper functions
  function sanitizeURISlug(slug) {
    if (!slug) return slug;
    return slug.replace('/', '%2F').replace('&', '%26');
  }

  function createResultRow(result) {
    const row = document.createElement('tr');

    const gameCode = result.cardInfo?.gameCode || result.gameCode;
    const setName = result.cardInfo?.setName || result.setName;
    const cardNumber = result.cardInfo?.number || result.cardNumber;
    const cardImage = result.cardInfo?.image;
    const cardName = result.cardName || result.cardInfo?.cardName;

    row.innerHTML = `
      <td class="image-cell">
        ${cardImage ? `
          <div class="card-image-container">
            <img src="${esc(cardImage)}" alt="Card" class="card-thumbnail" loading="lazy" />
          </div>
        ` : '-'}
      </td>
      <td>
        ${result.gradedId ?
          `<a href="https://pcgpopreport.com/report/${encodeURIComponent(result.gradedId)}"
              class="grade-id"
              target="_blank">${esc(result.gradedId)}</a>`
          : '-'}
      </td>
      <td>
        ${gameCode ?
          `<a href="https://pcgpopreport.com/${esc(sanitizeURISlug(gameCode))}"
              target="_blank">${esc(result.game || gameCode)}</a>`
          : '-'}
      </td>
      <td>
        ${gameCode && setName && cardNumber ?
          `<a href="https://pcgpopreport.com/${esc(sanitizeURISlug(gameCode))}/${esc(sanitizeURISlug(setName))}/${esc(sanitizeURISlug(cardNumber))}"
              target="_blank">${esc(cardName)}</a>`
          : esc(cardName || '-')}
      </td>
      <td>
        ${gameCode && setName ?
          `<a href="https://pcgpopreport.com/${esc(sanitizeURISlug(gameCode))}/${esc(sanitizeURISlug(setName))}"
              target="_blank">${esc(setName)}</a>`
          : esc(setName || '-')}
      </td>
      <td class="grade-value">
        ${result.finalGrade ? `
          <span class="grade-badge">${esc(result.finalGrade)}</span>
          <span class="grade-type">${esc(result.grade)}</span>
        ` :
        `<span class="grade-type">Total Graded: ${esc(result.cardInfo?.cardsGraded || '0')}</span>`}
      </td>
    `;

    return row;
  }

  // UI Update functions
  function updateUIFromState(state) {
    const {
      currentPage,
      totalPages,
      totalElements,
      isLoading,
      itemsPerPage
    } = state;

    const prevButton = $('.pagination-prev');
    const nextButton = $('.pagination-next');
    const paginationCurrent = $('.pagination-current');

    if (prevButton) prevButton.disabled = currentPage === 0 || isLoading;
    if (nextButton) nextButton.disabled = currentPage >= totalPages - 1 || isLoading;
    if (paginationCurrent) {
      paginationCurrent.textContent = `Page ${currentPage + 1} of ${totalPages || 1}`;
    }

    const modalBody = $('.modal-body');
    const searchLoadingIndicator = $('.search-loading-indicator');
    const loadingSpinner = $('.loading-spinner');

    if (modalBody) {
      modalBody.classList.toggle('pagination-loading', isLoading);
    }
    if (searchLoadingIndicator) {
      searchLoadingIndicator.classList.toggle('active', isLoading);
    }
    if (loadingSpinner) {
      loadingSpinner.style.display = isLoading ? 'block' : 'none';
    }
    if (state.currentSearchTerm) {
      updateModalTitle(state.currentSearchTerm);
    }

    updatePaginationInfo(currentPage, itemsPerPage, totalElements);
  }

  function updatePaginationInfo(page, limit, total) {
    const start = $('#results-start');
    const end = $('#results-end');
    const totalSpan = $('#results-total');

    if (!start || !end || !totalSpan) return;

    if (!total) {
      start.textContent = '0';
      end.textContent = '0';
      totalSpan.textContent = '0';
      return;
    }

    const startNum = page * limit + 1;
    const endNum = Math.min((page + 1) * limit, total);

    start.textContent = startNum;
    end.textContent = endNum;
    totalSpan.textContent = total;
  }

  function updateSearchResults(data) {
    const searchResults = $('#search-results');
    if (!searchResults) return;

    searchResults.innerHTML = '';

    if (data.data && data.data.length > 0) {
      data.data.forEach(result => {
        searchResults.appendChild(createResultRow(result));
      });
    } else {
      const noResultsRow = document.createElement('tr');
      noResultsRow.innerHTML = `
        <td colspan="6" class="search-results-empty">
          No results found
        </td>
      `;
      searchResults.appendChild(noResultsRow);
    }
  }

  function showErrorMessage(message) {
    const searchResults = $('#search-results');
    if (!searchResults) return;

    const defaultError = modal.dataset.errorMessage || 'Error loading results';

    searchResults.innerHTML = `
      <tr>
        <td colspan="6" class="search-results-empty">
          ${esc(message || defaultError)}
        </td>
      </tr>
    `;

    updatePaginationInfo(0, searchState.itemsPerPage, 0);
  }

  async function performSearch(searchTerm, page = 0) {
    const { itemsPerPage } = searchState;

    try {
      SearchStateManager.setState({ isLoading: true });

      const queryString = `cardName=${encodeURIComponent(searchTerm)}&setName=${encodeURIComponent(searchTerm)}&gradedId=${encodeURIComponent(searchTerm)}`;
      const response = await fetch(`${SEARCH_API}?${queryString}&offset=${page * itemsPerPage}&limit=${itemsPerPage}`);

      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`);
      }

      const data = await response.json();

      updateModalTitle(searchTerm);

      SearchStateManager.setState({
        currentPage: page,
        totalElements: data.totalElements,
        totalPages: Math.ceil(data.totalElements / itemsPerPage),
        currentSearchTerm: searchTerm
      });

      updateSearchResults(data);
      return data;
    } catch (error) {
      console.error('Search error:', error);
      showErrorMessage(error.message);
      SearchStateManager.setState({
        currentPage: 0,
        totalElements: 0,
        totalPages: 0
      });
      return null;
    } finally {
      SearchStateManager.setState({ isLoading: false });
    }
  }

  function debouncedSearch(searchTerm) {
    clearTimeout(searchState.searchTimeout);

    return new Promise((resolve) => {
      searchState.searchTimeout = setTimeout(async () => {
        const data = await performSearch(searchTerm);
        resolve(data);
      }, 300);
    });
  }

  function isOpen() {
    return !!modal && modal.classList.contains('is-open');
  }

  function open(term) {
    if (!modal) return;
    if (!isOpen()) {
      lastOpener = document.activeElement;
      modal.classList.add('is-open');
      document.documentElement.classList.add('pcg-cert-search-open');
    }
    const searchTerm = typeof term === 'string' ? term.trim() : '';
    if (modalSearchInput) {
      if (searchTerm) modalSearchInput.value = searchTerm;
      modalSearchInput.focus();
      if (!searchTerm) modalSearchInput.select();
    }
    if (searchTerm.length >= 2) {
      SearchStateManager.setState({ currentSearchTerm: searchTerm });
      debouncedSearch(searchTerm);
    }
  }

  function close() {
    if (!isOpen()) return;
    modal.classList.remove('is-open');
    document.documentElement.classList.remove('pcg-cert-search-open');
    if (lastOpener && document.contains(lastOpener) && typeof lastOpener.focus === 'function') {
      lastOpener.focus();
    }
    lastOpener = null;
  }

  function isTextField(el) {
    if (!el) return false;
    const tag = el.tagName;
    return el.isContentEditable || tag === 'TEXTAREA' || tag === 'SELECT' ||
      (tag === 'INPUT' && !/^(button|checkbox|radio|submit|reset|range|color|file|image)$/i.test(el.type));
  }

  function setupKeyboard() {
    document.addEventListener('keydown', (e) => {
      // Ctrl+K / Cmd+K anywhere, "/" outside a text field: open the cert lookup.
      if ((e.ctrlKey || e.metaKey) && !e.altKey && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        isOpen() ? modalSearchInput && modalSearchInput.focus() : open();
        return;
      }
      if (e.key === '/' && !e.ctrlKey && !e.metaKey && !e.altKey && !isOpen() && !isTextField(e.target)) {
        e.preventDefault();
        open();
        return;
      }

      if (!isOpen()) return;

      if (e.key === 'Escape') {
        close();
        return;
      }

      // Arrow keys page the results, except while the caret is in a text field.
      if (isTextField(e.target)) return;

      const { currentPage, totalPages, isLoading } = SearchStateManager.getState();

      if (isLoading) return;

      switch (e.key) {
        case 'ArrowLeft':
          if (currentPage > 0) {
            performSearch(searchState.currentSearchTerm, currentPage - 1);
          }
          break;
        case 'ArrowRight':
          if (currentPage < totalPages - 1) {
            performSearch(searchState.currentSearchTerm, currentPage + 1);
          }
          break;
      }
    });
  }

  function setupModal() {
    const modalSearchButton = $('.modal-search-button');
    const modalClose = $('.modal-close');
    const prevButton = $('.pagination-prev');
    const nextButton = $('.pagination-next');

    let modalSearchDebounce = null;
    if (modalSearchInput && modalSearchButton) {
      modalSearchInput.addEventListener('input', async (e) => {
        const searchTerm = e.target.value.trim();

        if (searchTerm.length < 2) return;

        clearTimeout(modalSearchDebounce);

        modalSearchDebounce = setTimeout(async () => {
          SearchStateManager.setState({ currentSearchTerm: searchTerm });
          await performSearch(searchTerm);
        }, 500);
      });

      modalSearchInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          // Clear any pending debounced search
          clearTimeout(modalSearchDebounce);
          const searchTerm = modalSearchInput.value.trim();
          if (searchTerm.length >= 2 && !searchState.isLoading) {
            SearchStateManager.setState({ currentSearchTerm: searchTerm });
            performSearch(searchTerm);
          }
        }
      });

      modalSearchButton.addEventListener('click', () => {
        clearTimeout(modalSearchDebounce);
        const searchTerm = modalSearchInput.value.trim();
        if (searchTerm.length >= 2 && !searchState.isLoading) {
          SearchStateManager.setState({ currentSearchTerm: searchTerm });
          performSearch(searchTerm);
        }
      });
    }

    // Setup pagination handlers
    if (prevButton) {
      prevButton.addEventListener('click', () => {
        const { currentPage, isLoading } = SearchStateManager.getState();
        if (!isLoading && currentPage > 0) {
          performSearch(searchState.currentSearchTerm, currentPage - 1);
        }
      });
    }

    if (nextButton) {
      nextButton.addEventListener('click', () => {
        const { currentPage, totalPages, isLoading } = SearchStateManager.getState();
        if (!isLoading && currentPage < totalPages - 1) {
          performSearch(searchState.currentSearchTerm, currentPage + 1);
        }
      });
    }

    if (modalClose) modalClose.addEventListener('click', close);

    modal.addEventListener('click', (e) => {
      if (e.target === modal) close();
    });
  }

  // The home page's search panel (Count-timer-section): typing opens the modal on that term.
  function setupInlineSearch(mainSearchInput) {
    const searchForm = mainSearchInput.closest('form');
    const resetButton = searchForm ? searchForm.querySelector('.reset__button') : null;

    if (searchForm) {
      searchForm.addEventListener('submit', (e) => {
        e.preventDefault();
        const searchTerm = mainSearchInput.value.trim();
        if (searchTerm.length >= 2) open(searchTerm);
        return false;
      });
    }

    if (resetButton) {
      resetButton.classList.add('hidden');
    }

    let mainSearchDebounce = null;
    mainSearchInput.addEventListener('input', async (e) => {
      const searchTerm = e.target.value.trim();

      if (resetButton) {
        resetButton.classList.toggle('hidden', !searchTerm);
      }

      if (modalSearchInput) {
        modalSearchInput.value = searchTerm;
      }

      clearTimeout(mainSearchDebounce);

      if (searchTerm.length < 2) {
        close();
        return;
      }

      mainSearchDebounce = setTimeout(() => open(searchTerm), 500);
    });

    if (resetButton) {
      resetButton.addEventListener('click', () => {
        mainSearchInput.value = '';
        close();
        resetButton.classList.add('hidden');
      });
    }
  }

  function init() {
    modal = document.getElementById('search-modal-popreport');
    if (!modal) return;
    // At the end of <body>, so no section's stacking context can sit over the modal.
    if (modal.parentElement !== document.body) document.body.appendChild(modal);
    modalSearchInput = $('#modal-search-input');

    SearchStateManager.subscribe(updateUIFromState);
    setupModal();
    setupKeyboard();

    document.addEventListener('click', (e) => {
      const opener = e.target.closest && e.target.closest('[data-pcg-cert-open]');
      if (!opener) return;
      e.preventDefault();
      open();
    });

    document.querySelectorAll('[data-pcg-cert-input]').forEach(setupInlineSearch);
  }

  window.PCGCertSearch = { open, close, isOpen };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
